import type { QuestionType } from '@l2u/shared/schemas';
import type { Json } from '@l2u/web-shared';

// The pure half of batched step saving (spec 2026-09-07 §3.2): which answers
// are staged, how the draft is keyed and mirrored to localStorage, what the
// bulk PUT body looks like, and how a failed save is reported. Kept out of
// Questionnaire.tsx so it unit-tests without a DOM (pattern: stepBlockers.ts,
// driverNav.ts).
//
// The draft is a CRASH-RECOVERY BUFFER, not storage of record: per viewer,
// per device, and only ever holding what has not yet reached the server.

export type StagedAnswer = { answer: Json | null; comment: string | null };
export type StagedAnswers = ReadonlyMap<string, StagedAnswer>;

/**
 * Types whose save is deferred to "Save & continue". Everything else —
 * uploads, adoptions, signatures — performs a side effect (a file in signed
 * storage, an adoption row) that cannot be replayed from a draft, and keeps
 * saving immediately (spec §3.3).
 */
export const STAGED_QUESTION_TYPES: readonly QuestionType[] = [
  'yesno',
  'text',
  'short_text',
  'select',
  'multiselect',
];

export function isStagedQuestionType(type: QuestionType): boolean {
  return STAGED_QUESTION_TYPES.includes(type);
}

/**
 * Types the vendor TYPES into, as opposed to clicks once. The distinction is
 * not cosmetic: a click is a whole decision and can be sent as it happens,
 * while a keystroke is one character of an answer that is not finished yet, so
 * it has to be coalesced before it is sent (see answerPersistMode).
 */
export const TYPED_QUESTION_TYPES: readonly QuestionType[] = ['text', 'short_text'];

export function isTypedQuestionType(type: QuestionType): boolean {
  return TYPED_QUESTION_TYPES.includes(type);
}

/**
 * How long typed input is coalesced before the immediate-save path sends it.
 * Only reached by a returned (rejected) answer — everything else typed is
 * staged for the step flush and sends nothing until "Save & continue".
 */
export const COMMIT_DEBOUNCE_MS = 800;

/**
 * How one answer is persisted:
 *
 * - `stage` — held for the step-level flush, mirrored to the local draft.
 * - `commit-debounced` — sent on its own, but only once the vendor stops
 *   typing (and always flushed on blur and on unmount).
 * - `commit-now` — sent as it happens.
 *
 * A returned (rejected) answer never stages: its next action is the
 * per-question "Submit to admin", which posts against the SERVER's row, and on
 * a submitted questionnaire the step renders neither Continue nor Submit, so a
 * staged edit there would have nothing left to flush it. But committing is not
 * a licence to send a request per keystroke — react-query's `mutate` does not
 * serialise, so racing PUTs can land out of order and store a PREFIX of what
 * the vendor typed while the screen shows the whole of it. Typed input on the
 * commit path is coalesced; a click is not, because there is nothing to
 * coalesce and delaying it would only widen the window in which it is lost.
 */
export type PersistMode = 'stage' | 'commit-debounced' | 'commit-now';

export function answerPersistMode(type: QuestionType, isReturnedAnswer: boolean): PersistMode {
  if (isStagedQuestionType(type) && !isReturnedAnswer) return 'stage';
  return isTypedQuestionType(type) ? 'commit-debounced' : 'commit-now';
}

/** Every draft this portal writes starts with this, so sign-out can find them all. */
export const DRAFT_KEY_PREFIX = 'l2u-draft:';

/**
 * Per ACCOUNT, per portal (the api prefix) and per questionnaire. The asset
 * wizard and a vendor with a single questionnaire have no selected id, so they
 * share one bucket under 'all'.
 *
 * The account id is not decoration. `apiPrefix` is a constant per portal —
 * every driver on a device shares `/api/driver/me` — so without it a shared
 * depot tablet would restore the previous driver's answers into the next
 * driver's wizard, tell them the answers were their own, and write them into
 * the wrong compliance record on the next Continue. Cross-account
 * CONTAMINATION is worse than the loss the draft exists to prevent, so no
 * account id means NO KEY: a null return is the caller's instruction to keep
 * no draft at all rather than to share one anonymous bucket.
 *
 * The id is the authenticated subject already carried in this browser's JWT —
 * an identifier, not a secret.
 */
export function draftStorageKey(
  accountId: string | null,
  apiPrefix: string,
  questionnaireId: string | null,
): string | null {
  if (!accountId) return null;
  return `${DRAFT_KEY_PREFIX}${accountId}:${apiPrefix}:${questionnaireId ?? 'all'}`;
}

type DraftFile = { v: 1; answers: Record<string, StagedAnswer> };

export function serialiseDraft(staged: StagedAnswers): string {
  const answers: Record<string, StagedAnswer> = {};
  for (const [id, entry] of staged) answers[id] = entry;
  const file: DraftFile = { v: 1, answers };
  return JSON.stringify(file);
}

/**
 * Tolerant on purpose: junk, a foreign version, a non-object or an entry
 * without an `answer` key yields nothing rather than a throw. A corrupt
 * recovery buffer must never take the wizard down with it.
 */
export function parseDraft(raw: string | null): Map<string, StagedAnswer> {
  const out = new Map<string, StagedAnswer>();
  if (!raw) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  if (!parsed || typeof parsed !== 'object' || (parsed as { v?: unknown }).v !== 1) return out;
  const answers = (parsed as { answers?: unknown }).answers;
  if (!answers || typeof answers !== 'object') return out;
  for (const [id, value] of Object.entries(answers as Record<string, unknown>)) {
    if (!value || typeof value !== 'object' || !('answer' in value)) continue;
    const entry = value as { answer?: unknown; comment?: unknown };
    out.set(id, {
      answer: (entry.answer ?? null) as Json | null,
      comment: typeof entry.comment === 'string' ? entry.comment : null,
    });
  }
  return out;
}

/** The three Storage methods this module needs — typed narrowly so tests can stand one in. */
export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Plus the two that let sign-out find every draft key without knowing any of them. */
export type EnumerableStorageLike = StorageLike & Pick<Storage, 'length' | 'key'>;

/**
 * `window.localStorage` is not merely unusable where site data is blocked —
 * READING THE PROPERTY throws in that case, before any method is called. So
 * even reaching storage needs a guard, and a browser that refuses it is
 * reported as no storage rather than as a crash on boot.
 */
export function browserDraftStorage(): EnumerableStorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readDraft(
  storage: StorageLike | null,
  key: string | null,
): Map<string, StagedAnswer> {
  if (!storage || !key) return new Map();
  try {
    return parseDraft(storage.getItem(key));
  } catch {
    return new Map();
  }
}

/**
 * Mirror the map; an empty map removes the key so a finished step leaves
 * nothing behind.
 *
 * Returns whether the mirror now reflects `staged`. The throw is still
 * swallowed — a full or blocked storage must never take the wizard down — but
 * it is no longer swallowed SILENTLY: this mirror is the whole of the
 * data-loss safety now that per-keystroke saving is gone, so a vendor whose
 * browser is not keeping it has to be told, or they close a tab on work that
 * was never sent anywhere.
 */
export function writeDraft(
  storage: StorageLike | null,
  key: string | null,
  staged: StagedAnswers,
): boolean {
  if (!storage || !key) return false;
  try {
    if (staged.size === 0) storage.removeItem(key);
    else storage.setItem(key, serialiseDraft(staged));
    return true;
  } catch {
    return false;
  }
}

/** Every draft key currently held, in storage order. Tolerant of a throwing storage. */
export function draftKeysIn(storage: EnumerableStorageLike | null): string[] {
  if (!storage) return [];
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && key.startsWith(DRAFT_KEY_PREFIX)) keys.push(key);
    }
    return keys;
  } catch {
    return [];
  }
}

/**
 * Drop every draft this browser holds, for every account and portal.
 *
 * Called on sign-out. Keying drafts by account already stops one account
 * READING another's, but a draft left behind on a shared device outlives the
 * session that made it indefinitely, and unsent compliance answers are not
 * something to leave lying on a depot tablet. Deliberately not scoped to the
 * account signing out: the id is gone by the time some sign-out paths run, and
 * there is nothing on this device worth keeping once nobody is signed in.
 */
export function clearAllDrafts(storage: EnumerableStorageLike | null): void {
  if (!storage) return;
  for (const key of draftKeysIn(storage)) {
    try {
      storage.removeItem(key);
    } catch {
      // Nothing useful to do: sign-out must complete regardless.
    }
  }
}

/**
 * The most answers one bulk request may carry — the endpoint's own ceiling
 * (SubmissionAnswerBatchInput in @l2u/shared). Kept here as well because the
 * client has to split BEFORE it sends: the endpoint's reply to an oversized
 * body is a 422 with no `persisted_question_ids`, so a step with more staged
 * answers than this would fail whole, every time, with no way to advance.
 */
export const MAX_ANSWERS_PER_REQUEST = 100;

/**
 * Split the staged map into request-sized pieces, in staging order.
 *
 * An empty map yields NO chunks at all, which is what stops the caller ever
 * issuing the empty request the endpoint's `min(1)` rejects — clicking
 * "Continue" with nothing staged must cost no request, not a 422.
 */
export function chunkStaged(
  staged: StagedAnswers,
  size: number = MAX_ANSWERS_PER_REQUEST,
): Map<string, StagedAnswer>[] {
  const limit = Math.max(1, Math.floor(size));
  const chunks: Map<string, StagedAnswer>[] = [];
  let current = new Map<string, StagedAnswer>();
  for (const [id, entry] of staged) {
    current.set(id, entry);
    if (current.size === limit) {
      chunks.push(current);
      current = new Map();
    }
  }
  if (current.size > 0) chunks.push(current);
  return chunks;
}

/** Body of PUT <prefix>/submissions, in staging order. */
export function batchBody(staged: StagedAnswers): {
  answers: Array<{ question_id: string; answer: Json | null; comment: string | null }>;
} {
  return {
    answers: [...staged].map(([question_id, entry]) => ({
      question_id,
      answer: entry.answer,
      comment: entry.comment,
    })),
  };
}

export type StepSaveRequest = { path: string; body: ReturnType<typeof batchBody> };

/**
 * Every request one "Save & continue" sends: a PUT <prefix>/submissions per
 * chunk, in staging order, each carrying the chunk it was built from so the
 * caller can account for what the server took. One request for any ordinary
 * step, and NONE when nothing is staged.
 *
 * The same for all three wizards — company (/api/me), driver (/api/driver/me)
 * and asset (/api/me/assets/:id). The asset prefix used to lack the bulk route
 * and sent one awaited request per answer instead, which is what made asset
 * steps the slowest save in the portal.
 */
export function stepSaveRequests(
  apiPrefix: string,
  staged: StagedAnswers,
): Array<StepSaveRequest & { chunk: Map<string, StagedAnswer> }> {
  return chunkStaged(staged).map((chunk) => ({
    chunk,
    path: `${apiPrefix}/submissions`,
    body: batchBody(chunk),
  }));
}

export type BatchFailure = { failedQuestionId: string | null; persistedQuestionIds: string[] };

/**
 * The server's failure report, read off an ApiError's `details`. Tolerant of
 * a body without one — a network error, or a 422 from validation — in which
 * case nothing persisted and the whole draft is kept.
 */
export function batchFailureFromDetails(details: unknown): BatchFailure {
  const d = (details && typeof details === 'object' ? details : {}) as {
    failed_question_id?: unknown;
    persisted_question_ids?: unknown;
  };
  return {
    failedQuestionId: typeof d.failed_question_id === 'string' ? d.failed_question_id : null,
    persistedQuestionIds: Array.isArray(d.persisted_question_ids)
      ? d.persisted_question_ids.filter((x): x is string => typeof x === 'string')
      : [],
  };
}

/**
 * Drop the entries a request confirmed, keeping anything the vendor changed
 * while it was in flight.
 *
 * Identity is the test, not the id: the wizard stores a fresh entry object on
 * every keystroke, so an entry that is still the very object that was sent is
 * unchanged and safe to drop, while one that is not holds an answer the server
 * has never seen. Clearing the whole map on success instead would silently
 * discard exactly those — a staged answer that is never sent. Never mutates.
 */
export function withoutSent(
  staged: StagedAnswers,
  sent: StagedAnswers,
): Map<string, StagedAnswer> {
  const next = new Map(staged);
  for (const [id, entry] of sent) {
    if (next.get(id) === entry) next.delete(id);
  }
  return next;
}

/**
 * Which question a rejected request was about.
 *
 * The bulk endpoint says so in `details`. A failure that carries no details —
 * a network error, a bodyless 500 — does not, but when the chunk being sent
 * held exactly one answer the id is still known: a single-entry chunk names
 * itself. A multi-entry chunk does not, and guessing at one of its members
 * would name the wrong question, which is worse than naming none.
 */
export function resolveFailedQuestionId(
  failure: BatchFailure,
  chunk: StagedAnswers,
): string | null {
  if (failure.failedQuestionId) return failure.failedQuestionId;
  if (chunk.size !== 1) return null;
  return [...chunk.keys()][0] ?? null;
}

export function failedSaveMessage(
  failure: BatchFailure,
  labelFor: (questionId: string) => string | undefined,
  serverMessage: string,
): string {
  const label = failure.failedQuestionId ? labelFor(failure.failedQuestionId) : undefined;
  if (label) {
    return `Could not save "${label}": ${serverMessage}. Your other answers are kept — fix this one and try again.`;
  }
  return `Could not save this step: ${serverMessage}. Your answers are kept — try again.`;
}

/** Spec §3.2 wording; null when nothing was restored, so nothing renders. */
export function restoredDraftNote(count: number): string | null {
  return count > 0 ? "Restored answers you hadn't saved yet." : null;
}

/** The step button carries the pending state the per-answer indicator used to. */
export function continueLabel(stagedCount: number): string {
  return stagedCount > 0 ? 'Save & continue →' : 'Continue →';
}
